import { isValid as isValidDate } from 'date-fns';
import { FC, useCallback, useContext } from 'react';
import { Prompt } from 'react-router-dom';
import { Registration } from '@stax-core/types-omni';
import { RegistrationForm } from '../../@types';
import { coreapi } from '../../api';
import {
  DateInput,
  FieldError,
  Group,
  Input,
  ToggleableField,
  ToggleableFieldProps,
} from '../../components';
import {
  AuthStore,
  BrandStore,
  RegistrationStore,
  updateRegistration,
} from '../../context';
import { useToaster, useWriteableFieldFormValidation } from '../../hooks';
import { formatPhoneNumber } from '../../util/format.util';
import {
  isMeaningfulString,
  isValidPhoneNumber,
} from '../../util/validator.util';
import CenteredContent from '../components/CenteredContent';
import { BackButton } from '../components/BackButton';
import { NextButton } from '../components/NextButton';
import { SsnInput } from '../../components/SsnInput';
import { MainForm } from '../components/application/MainForm';

interface FieldValues
  extends Pick<
    Registration,
    | 'first_name'
    | 'last_name'
    | 'phone_number'
    | 'user_dob'
    | 'user_ssn'
    | 'owner_address_1'
    | 'owner_address_2'
  > {}

export interface PersonalDetailsProps extends RegistrationForm<FieldValues> {}

const PersonalDetailsFields: FC<PersonalDetailsProps> = props => {
  const {
    state: { brand },
  } = useContext(BrandStore);

  const defaultFieldProps: Partial<ToggleableFieldProps<any, HTMLElement>> = {
    toggleable: props.mode === 'review',
    defaultMode: props.mode === 'review' ? 'display' : 'edit',
  };

  return (
    <>
      <div data-testid="personal-details-1">
        <Group direction="column">
          <ToggleableField
            name="first_name"
            label="Legal First Name"
            {...defaultFieldProps}
          >
            {props => <Input {...props.input} />}
          </ToggleableField>
          <FieldError name="first_name" />
        </Group>

        <Group direction="column">
          <ToggleableField
            name="last_name"
            label="Legal Last Name"
            {...defaultFieldProps}
          >
            {props => <Input {...props.input} />}
          </ToggleableField>
          <FieldError name="last_name" />
        </Group>

        <Group direction="column">
          <ToggleableField
            name="phone_number"
            label="Phone Number"
            parse={formatPhoneNumber}
            format={formatPhoneNumber}
            {...defaultFieldProps}
          >
            {props => <Input {...props.input} type="tel" maxLength={14} />}
          </ToggleableField>
          <FieldError name="phone_number" />
        </Group>

        <Group direction="column">
          <ToggleableField
            name="user_dob"
            label="Birth Date"
            {...defaultFieldProps}
          >
            {props => <DateInput {...props.input} />}
          </ToggleableField>
          <FieldError name="user_dob" />
        </Group>

        <Group direction="column">
          <ToggleableField
            name="user_ssn"
            label="Social Security Number"
            secure
            {...defaultFieldProps}
          >
            {props => (
              <SsnInput
                {...props.input}
                autoComplete="off"
                tooltip={`To confirm that you are who you say you are and that you have permission to operate on behalf of an entity, ${brand?.platformName} must request your SSN.`}
              />
            )}
          </ToggleableField>
          <FieldError name="user_ssn" />
        </Group>
      </div>
      {props.children}
    </>
  );
};

export const PersonalDetailsPage: FC = () => {
  const {
    dispatch,
    state: { registration },
  } = useContext(RegistrationStore);
  const { toaster, toast } = useToaster();
  const {
    state: { auth },
  } = useContext(AuthStore);
  const authToken = auth?.token ?? null;

  const onSubmit = useCallback(
    async (formValues: FieldValues) => {
      const payload: Partial<Registration> = {
        first_name: formValues.first_name,
        last_name: formValues.last_name,
        phone_number: formValues.phone_number,
        user_dob: formValues.user_dob,
        user_ssn: formValues.user_ssn,
      };

      try {
        const r = await coreapi.put(authToken!, '/team/registration', payload);
        dispatch(updateRegistration(r));
        return true;
      } catch (error) {
        toaster(toast.error(error, 'There was a problem saving your changes.'));
      }
    },
    [authToken, dispatch, toast, toaster],
  );

  const validate = useWriteableFieldFormValidation((values: FieldValues) => {
    const errors: Partial<Record<keyof FieldValues, any>> = {};
    if (!isMeaningfulString(values.first_name)) {
      errors.first_name = 'Not a valid name';
    }
    if (!isMeaningfulString(values.last_name)) {
      errors.last_name = 'Not a valid name';
    }

    if (values.phone_number && !isValidPhoneNumber(values.phone_number)) {
      errors.phone_number = 'Please enter a valid phone number';
    }
    const userDob = values.user_dob ? new Date(values.user_dob) : null;
    if (!userDob || !isValidDate(userDob) || userDob > new Date()) {
      errors.user_dob = 'Not a valid date';
    }
    if (values.user_ssn && values.user_ssn.length < 9) {
      errors.user_ssn = 'Social Security Number must be 9 digits';
    }
    if (
      !values.user_ssn ||
      /[^0-9\-]/.test(values.user_ssn) ||
      values.user_ssn.replace(/[^0-9]/g, '').length !== 9
    ) {
      errors.user_ssn = 'Please enter a valid 9 digit Social Security Number.';
    }
    return errors;
  });

  return (
    <MainForm<FieldValues>
      initialValues={mapInitialFormValues(registration)}
      validate={validate}
      onSubmit={onSubmit}
    >
      {formProps => (
        <CenteredContent
          id="personal-details"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 100 }}
          exit={{ opacity: 0, y: -20 }}
        >
          <BackButton id="personal-details-1-back" />
          <h2>Personal details</h2>
          <div>
            <p>
              We need your name to verify your identity. Please use what is on
              your offical ID.
            </p>
            <PersonalDetailsFields mode="edit" {...formProps}>
              <Prompt
                when={formProps.dirty && !formProps.submitting}
                message="You have unsaved changes; are you sure you want to leave without saving?"
              />
            </PersonalDetailsFields>
          </div>
          <NextButton id="personal-details-1-continue" formProps={formProps} />
        </CenteredContent>
      )}
    </MainForm>
  );
};

function mapInitialFormValues(
  registration: Registration | null,
): Partial<FieldValues> {
  /** Copied from FinancialFields.util mapRegistrationToInitialFormValues() */
  return registration
    ? {
        first_name: registration.first_name,
        last_name: registration.last_name,
        phone_number: registration.phone_number,
        user_dob: registration.user_dob,
        user_ssn: registration.user_ssn,
      }
    : ({} as any);
}
